import { icon } from './icons.js';
import { folderPath, folderEntries, storageOf, destinationFolder } from '../../../../engine/core/vault-folders.js';
import { confirmAction } from './dialogs.js';
import { showToast } from '../toast.js';
/**
 * Vault List Module
 * Document list rendering, folder tree, drag-and-drop, resize handling
 */

import { updateDocumentSizes } from '../storage-usage.js';
import { invalidateStorageInfo } from '../../../../engine/services/storage-service.js';
import * as State from './state.js';
import * as Search from './search.js';
import * as Actions from './actions.js';
import { listAttachments, deleteAttachment, getAttachmentUrl } from '../../../../engine/services/attachment-service.js';
import { formatBytes } from '../../../../engine/services/storage-service.js';
import { saveFileFromBlob } from '../save-file.js';
import { withOperation } from '../operation-progress.js';
import { isOnlineSlotEnabled } from '../../../../engine/crypto/vault-storage.js';
import { canMoveToDevice } from '../../../../engine/services/move-to-device.js';
import { t, formatDate as formatLocalDate } from '../../../../shared/i18n/index.js';

// ============================================
// Dependencies (injected via init)
// ============================================

let _deps = {
  // None needed - uses State for everything
};

/**
 * Initialize list module with dependencies
 * @param {Object} deps - Dependencies (currently unused)
 */
export function initList(deps) {
  // Reserved for future use
}

// ============================================
// Utilities
// ============================================

// Canonical implementations live in engine/core/validation.js (the previous local
// DOM-based escapeHtml did not escape quotes). Re-exported because external
// callers import escapeHtml from this module.
export { escapeHtml } from "../../../../engine/core/validation.js";
import { escapeHtml, escapeAttr } from "../../../../engine/core/validation.js";

/**
 * Format timestamp as relative time
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @returns {string} Formatted relative time
 */
export function formatDate(timestamp) {
  const now = Date.now();
  const diff = now - timestamp;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (mins < 1) return t('vault.date.justNow');
  if (mins < 60) return t('vault.date.minutesAgo', { count: mins });
  if (hours < 24) return t('vault.date.hoursAgo', { count: hours });
  if (days < 7) return t('vault.date.daysAgo', { count: days });
  return formatLocalDate(new Date(timestamp), { year: 'numeric', month: 'numeric', day: 'numeric' });
}

/**
 * Get folder path as array of folder objects
 * @param {string} folderId - Folder ID
 * @param {Object} index - Vault index
 * @returns {Array} Array of folder objects from root to target
 */
export function getFolderPath(folderId, index) { return folderPath(index.folders, folderId); }

/**
 * Get folder path as string
 * @param {string} folderId - Folder ID
 * @param {Object} index - Vault index
 * @returns {string} Path string like "Root / Subfolder / Target"
 */
export function getFolderPathString(folderId, index) {
  const path = getFolderPath(folderId, index);
  return path.map((f) => f.name).join(" / ");
}

/** A member role ("editor", "viewer"…) in the current language. */
export function roleLabel(role) {
  return ['owner', 'editor', 'viewer'].includes(role) ? t(`vault.role.${role}`) : String(role || '');
}

// ============================================
// Main Render Function
// ============================================

/**
 * Render the vault panel content
 */
export function render() {
  const container = document.getElementById('vault-panel-content');
  if (!container || !State.isOpen()) return;
  const vault = State.getVaultStorage(), index = vault?.getIndex();
  if (!index) { container.innerHTML = `<div class="vault-empty-folder">${escapeHtml(t('vault.list.noVault'))}</div>`; return; }
  const scroll = container.querySelector('#vault-file-list')?.scrollTop || 0;
  const folderScroll = container.querySelector('#vault-folder-tree')?.scrollTop || 0;
  Search.updateSearchBox();
  const selected = State.getSelectedFolderId();
  const sizes = State.loadSizes();
  container.innerHTML = `
    <nav class="vault-col vault-col-folders" id="vault-col-folders" aria-label="${escapeAttr(t('vault.nav.label'))}" style="width:${sizes.foldersWidth}px">
      <div class="vault-col-scroll" id="vault-folder-tree">
        ${renderCategoryFolders()}
        <div class="vault-col-header"><span>${escapeHtml(t('vault.nav.folders'))}</span><button class="vault-col-btn" id="vault-new-folder" aria-label="${escapeAttr(t('vault.folder.new'))}" title="${escapeAttr(t('vault.folder.new'))}">${icon('plus')}</button></div>
        ${renderFolderTree(index)}
      </div>
      <div class="vault-col-footer"><button class="vault-account-btn" id="vault-import-data">${icon('upload')}<span>${escapeHtml(t('vault.data.import'))}</span></button>
        <button class="vault-account-btn" id="vault-export-data">${icon('download')}<span>${escapeHtml(t('vault.data.export'))}</span></button>
        <input type="file" id="vault-import-file" accept=".json,.zip" hidden>
      </div>
      <div class="vault-resize-handle vault-resize-handle-right" data-resize="folders"></div>
    </nav>
    <section class="vault-col vault-col-files" id="vault-col-files" aria-label="${escapeAttr(t('common.maps'))}">
      ${renderBreadcrumbs(index)}${renderSortBar()}
      <div class="vault-col-scroll" id="vault-file-list">${renderFileList(index, vault.getCurrentDocId())}</div>
    </section>`;
  attachEventListeners(); attachResizeListeners();
  // Preserve position for background size/share refreshes, reset when navigating.
  if (render.lastFolder === selected && render.lastQuery === State.getSearchQuery()) container.querySelector('#vault-file-list').scrollTop = scroll;
  container.querySelector('#vault-folder-tree').scrollTop = folderScroll;
  render.lastFolder = selected; render.lastQuery = State.getSearchQuery();
}

// ============================================
// Category Folders (Shared)
// ============================================

/**
 * Render category folders (Shared)
 * @returns {string} HTML string
 */
function renderCategoryFolders() {
  const docs = State.getVaultStorage().getIndex()?.docs || [];
  const shared = new Set([...docs.filter(d => d.role && d.role !== 'owner').map(d => d.docId), ...State.getOwnedSharesCache().map(s => s.id)]);
  return `<div class="vault-category-folders">${[
    [State.CATEGORY_ALL, t('vault.category.all'), 'map', docs.length],
    [State.CATEGORY_RECENT, t('vault.category.recent'), 'clock', null],
    [State.CATEGORY_SHARED, t('vault.category.shared'), 'share', shared.size],
  ].map(([id, name, glyph, count]) => `<button class="vault-category-row ${State.getSelectedFolderId() === id ? 'selected' : ''}" data-category-id="${id}" aria-current="${State.getSelectedFolderId() === id ? 'page' : 'false'}">${icon(glyph)}<span class="vault-category-name">${escapeHtml(name)}</span>${count !== null ? `<span class="vault-folder-badge">${count}</span>` : ''}</button>`).join('')}</div>`;
}

// ============================================
// Sort Bar
// ============================================

/**
 * Render the sort bar
 * @returns {string} HTML string
 */
function renderSortBar() {
  const sort = State.getSortBy(), order = State.getSortOrder();
  const sortLabel = (key, active) => t(`vault.sort.${key}${active ? (order === 'asc' ? 'Asc' : 'Desc') : ''}`);
  return `<div class="vault-sort-bar"><span>${escapeHtml(t(State.getSearchQuery() ? 'vault.list.searchResults' : 'vault.list.tagline'))}</span><div class="vault-sort-options" aria-label="${escapeAttr(t('vault.sort.label'))}">${[['name', t('common.name')], ['date', t('vault.sort.updated')]].map(([key,label]) => `<button class="vault-sort-btn ${sort === key ? 'active' : ''}" data-sort="${key}" aria-label="${escapeAttr(sortLabel(key, sort === key))}">${escapeHtml(label)} ${sort === key ? (order === 'asc' ? '↑' : '↓') : ''}</button>`).join('')}</div></div>`;
}

// ============================================
// Folder Tree
// ============================================

/**
 * Render folder tree
 * @param {Object} index - Vault index
 * @returns {string} HTML string
 */
function renderFolderTree(index) {
  const entries = folderEntries(index.folders), expanded = State.getExpandedFolders();
  return entries.filter(({ folder }) => folderPath(index.folders, folder.id).slice(0, -1).every(parent => parent.parentId === null || expanded.has(parent.id)))
    .map(({ folder, depth }) => renderFolderItem(folder, index, depth)).join('');
}

/**
 * Render a single folder item
 * @param {Object} folder - Folder object
 * @param {Object} index - Vault index
 * @param {number} depth - Nesting depth
 * @returns {string} HTML string
 */
function renderFolderItem(folder, index, depth) {
  const isRoot = folder.parentId === null;
  const expanded = isRoot || State.getExpandedFolders().has(folder.id);
  const hasChildren = index.folders.some(f => f.parentId === folder.id);
  const selected = folder.id === State.getSelectedFolderId();
  const count = index.docs.filter(d => d.folderId === folder.id).length;
  return `<div class="vault-folder-row ${selected ? 'selected' : ''}" data-folder-id="${escapeAttr(folder.id)}" style="--depth:${Math.min(depth, 8)}">
    ${hasChildren && !isRoot ? `<button class="vault-folder-expand" data-folder-id="${escapeAttr(folder.id)}" aria-label="${escapeAttr(t(expanded ? 'vault.folder.collapse' : 'vault.folder.expand', { name: folder.name }))}" aria-expanded="${expanded}">${expanded ? '⌄' : '›'}</button>` : '<span class="vault-folder-indent"></span>'}
    <button class="vault-folder-open" data-folder-id="${escapeAttr(folder.id)}" aria-current="${selected ? 'page' : 'false'}">${icon('folder')}<span class="vault-folder-name">${escapeHtml(folder.name)}</span>${isRoot ? `<small>${escapeHtml(t(storageOf(folder) === 'local' ? 'vault.storage.local' : 'vault.storage.online'))}</small>` : count ? `<span class="vault-folder-badge">${count}</span>` : ''}</button>
  </div>`;
}

// ============================================
// Breadcrumbs
// ============================================

/**
 * Render breadcrumbs for current folder
 * @param {Object} index - Vault index
 * @returns {string} HTML string
 */
function renderBreadcrumbs(index) {
  const selected = State.getSelectedFolderId();
  const folder = index.folders.find(f => f.id === selected);
  const path = getFolderPath(selected, index);
  const label = t(selected === State.CATEGORY_SHARED ? 'vault.category.shared' : selected === State.CATEGORY_RECENT ? 'vault.category.recentMaps' : 'vault.category.all');
  return `<div class="vault-breadcrumbs"><div class="vault-breadcrumbs-path">${path.length ? path.map((f,i) => `${i ? '<span class="vault-breadcrumb-sep">/</span>' : ''}<button class="vault-breadcrumb ${i === path.length - 1 ? 'current' : ''}" data-folder-id="${escapeAttr(f.id)}">${escapeHtml(f.name)}</button>`).join('') : `<h2>${escapeHtml(label)}</h2>`}</div>
    <div class="vault-toolbar">${folder ? `<details class="vault-folder-menu"><summary class="vault-toolbar-btn" aria-label="${escapeAttr(t('vault.folder.actions'))}">${icon('more')}</summary><div class="vault-folder-actions">
      <button class="vault-action-btn" data-action="new-subfolder" data-folder-id="${escapeAttr(folder.id)}">${icon('plus')}${escapeHtml(t('vault.folder.new'))}</button>
      <button class="vault-action-btn" data-action="rename" data-folder-id="${escapeAttr(folder.id)}">${icon('rename')}${escapeHtml(t('vault.folder.rename'))}</button>
      ${folder.parentId ? `<button class="vault-action-btn" data-action="move" data-folder-id="${escapeAttr(folder.id)}">${icon('move')}${escapeHtml(t('vault.folder.move'))}</button><button class="vault-action-btn danger" data-action="delete" data-folder-id="${escapeAttr(folder.id)}">${icon('trash')}${escapeHtml(t('vault.folder.delete'))}</button>` : ''}</div></details>` : ''}
      <button class="vault-toolbar-btn primary" id="vault-add-map">${icon('plus')}<span>${escapeHtml(t('vault.map.new'))}</span></button>
    </div></div>`;
}

// ============================================
// File List
// ============================================

/**
 * Render file list for current folder
 * @param {Object} index - Vault index
 * @param {string} currentDocId - Currently open document ID
 * @returns {string} HTML string
 */
function renderFileList(index, currentDocId) {
  const selected = State.getSelectedFolderId(), query = State.getSearchQuery().trim().toLowerCase();
  const sharedIds = new Set(State.getOwnedSharesCache().map(share => share.id));
  const matches = doc => !query || (doc.title || '').toLowerCase().includes(query);
  let docs = index.docs.filter(matches);
  let folders = query ? index.folders.filter(f => f.name.toLowerCase().includes(query)) : index.folders.filter(f => f.parentId === selected);
  if (!query) {
    if (selected === State.CATEGORY_SHARED) docs = docs.filter(d => (d.role && d.role !== 'owner') || sharedIds.has(d.docId));
    else if (selected !== State.CATEGORY_ALL && selected !== State.CATEGORY_RECENT) {
      docs = docs.filter(d => d.folderId === selected || (!d.folderId && index.folders.find(f => f.parentId === null && storageOf(f) === storageOf(d))?.id === selected));
    }
  }
  docs = selected === State.CATEGORY_RECENT && !query ? [...docs].sort((a,b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)).slice(0,20) : sortMaps(docs);
  const folderRows = folders.sort((a,b) => a.name.localeCompare(b.name)).map(folder => `<button class="vault-child-folder vault-search-folder" data-folder-id="${escapeAttr(folder.id)}">${icon('folder')}<span>${escapeHtml(folder.name)}${query ? `<small>${escapeHtml(getFolderPathString(folder.id, index))}</small>` : ''}</span>${icon('chevron')}</button>`).join('');
  return folderRows + docs.map(doc => renderMapItem(doc, currentDocId, true, !index.folders.some(f => f.id === selected) || !!query)).join('') ||
    `<div class="vault-empty-folder">${icon(query ? 'map' : 'folder')}<strong>${escapeHtml(t(query ? 'vault.list.noMatches' : 'vault.list.emptyTitle'))}</strong><span>${escapeHtml(t(query ? 'vault.list.noMatchesHint' : 'vault.list.emptyHint'))}</span></div>`;
}

/**
 * Sort maps based on current sort settings
 * @param {Array} maps - Array of map objects
 * @returns {Array} Sorted array
 */
function sortMaps(maps) {
  const sortBy = State.getSortBy();
  const sortOrder = State.getSortOrder();

  const sorted = [...maps];

  if (sortBy === "name") {
    sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  } else if (sortBy === "date") {
    sorted.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
  }

  if (sortOrder === "desc" && sortBy === "name") {
    sorted.reverse();
  } else if (sortOrder === "asc" && sortBy === "date") {
    sorted.reverse();
  }

  return sorted;
}

/**
 * Render shared documents list
 * @param {string} currentDocId - Currently open document ID
 * @returns {string} HTML string
 */


/**
 * Render a single map item
 * @param {Object} map - Map object
 * @param {string} currentDocId - Currently open document ID
 * @param {boolean} draggable - Whether item is draggable
 * @param {boolean} showPath - Whether to show folder path
 * @returns {string} HTML string
 */
export function renderMapItem(map, currentDocId, draggable, showPath) {
  const owner = !map.role || map.role === 'owner', current = map.docId === currentDocId;
  const storage = storageOf(map), id = escapeAttr(map.docId);
  const shared = State.getOwnedSharesCache().some(s => s.id === map.docId);
  const path = showPath && map.folderId ? getFolderPathString(map.folderId, State.getVaultStorage().getIndex()) : '';
  const title = map.title || t('common.untitled');
  const meta = [formatDate(new Date(map.updatedAt || map.createdAt || Date.now()).getTime())];
  if (path) meta.push(path);
  // Open by its link (not a member): kept while the link works.
  if (map.link) meta.push(roleLabel(map.role), t(map.linkGone ? 'share.link.goneShort' : 'share.link.viaLink'));
  else if (!owner) meta.push(roleLabel(map.role), t('vault.map.sharedWithYou'));
  else if (shared) meta.push(t('vault.map.sharedByYou'));
  if (storage === 'local') meta.push(t('vault.storage.local'));
  const action = (key, label, glyph, danger = false) => `<button class="vault-action-btn ${danger ? 'danger' : ''}" data-action="${key}" data-doc-id="${id}">${icon(glyph)}${escapeHtml(label)}</button>`;
  return `<div class="vault-map-wrapper"><div class="vault-map ${current ? 'active' : ''}" data-doc-id="${id}" data-storage="${storage}" draggable="${draggable && owner}">
    <button class="vault-map-open" data-open-doc="${id}" aria-label="${escapeAttr(t('vault.map.openLabel', { title }))}" ${current ? 'aria-current="true"' : ''}>${icon('map')}<span class="vault-map-info">
      <span class="vault-map-name">${escapeHtml(title)}${current ? `<span class="vault-current-badge">${escapeHtml(t('common.open'))}</span>` : ''}</span>
      <span class="vault-map-meta">${escapeHtml(meta.join(' · '))}<span class="vault-map-size" data-storage-doc-id="${id}">${escapeHtml(t('vault.size.loading'))}</span></span>
    </span></button>
    <button class="vault-map-expand" data-doc-id="${id}" aria-expanded="false" aria-label="${escapeAttr(t('vault.map.attachmentsFor', { title }))}" title="${escapeAttr(t('vault.map.attachments'))}">${icon('clip')}</button>
    <details class="vault-map-menu"><summary aria-label="${escapeAttr(t('vault.map.actionsFor', { title }))}" title="${escapeAttr(t('vault.map.actions'))}">${icon('more')}</summary><div class="vault-map-actions">
      ${owner ? action('rename', t('common.rename'), 'rename') + action('move', t('vault.map.move'), 'move') : ''}
      ${action('clone', t('common.duplicate'), 'copy')}${action('export', t('common.export'), 'download')}${action('details', t('vault.map.details'), 'info')}
      ${storage === 'online' && !map.link ? action('history', t('history.menu'), 'history') : ''}
      ${storage === 'local' && isOnlineSlotEnabled() ? action('promote', t('vault.map.moveOnline'), 'upload') : ''}
      ${canMoveToDevice(map.docId) ? action('move-to-device', t('vault.map.moveToDevice'), 'download') : ''}
      ${storage === 'online' && window.__TAURI_INTERNALS__ ? action('save-local', t('vault.map.saveLocally'), 'download') : ''}
      ${shared ? `<button class="vault-action-btn" data-action="manage-share" data-share-id="${id}">${icon('share')}${escapeHtml(t('vault.map.sharing'))}</button><button class="vault-action-btn" data-action="unshare" data-share-id="${id}">${escapeHtml(t('vault.map.stopSharing'))}</button>` : ''}
      ${owner ? action('delete', t('common.delete'), 'trash', true) : `<button class="vault-action-btn danger" data-action="leave-share" data-share-id="${id}">${icon('trash')}${escapeHtml(t(map.link ? 'share.link.holder.remove' : 'vault.map.leaveShare'))}</button>`}
    </div></details></div><div class="vault-map-attachments" data-doc-id="${id}"></div></div>`;
}

// ============================================
// Recent List
// ============================================

/**
 * Render recent maps list
 * @param {Object} index - Vault index
 * @param {string} currentDocId - Currently open document ID
 * @returns {string} HTML string
 */


// ============================================
// Event Listeners
// ============================================

/**
 * Attach event listeners to vault content
 */
function attachEventListeners() {
  const container = document.getElementById("vault-panel-content");
  if (!container) return;

  // Remove existing listeners first to prevent accumulation across re-renders
  // (the container element persists; only its innerHTML is replaced)
  container.removeEventListener("click", handleContainerClick);
  container.removeEventListener("dragstart", handleDragStart);
  container.removeEventListener("dragend", handleDragEnd);
  container.removeEventListener("dragover", handleDragOver);
  container.removeEventListener("drop", handleDrop);

  // Folder tree events
  container.addEventListener("click", handleContainerClick);
  container.addEventListener("dragstart", handleDragStart);
  container.addEventListener("dragend", handleDragEnd);
  container.addEventListener("dragover", handleDragOver);
  container.addEventListener("drop", handleDrop);

  // Import file input handler — use named function for proper removal
  const importFileInput = document.getElementById("vault-import-file");
  if (importFileInput) {
    importFileInput.removeEventListener("change", handleImportFileChange);
    importFileInput.addEventListener("change", handleImportFileChange);
  }

  // Load attachment sizes asynchronously (non-blocking)
  loadAttachmentSizes(container);
}

/**
 * Handle import file input change (named to allow proper removal)
 * @param {Event} e - Change event
 */
function handleImportFileChange(e) {
  const file = e.target.files?.[0];
  if (file) {
    Actions.handleImportData(file);
    // Reset input so the same file can be selected again
    e.target.value = "";
  }
}

/** Map and attachment sizes use one account request, including shared maps. */
async function loadAttachmentSizes(container) {
  for (const row of container.querySelectorAll('.vault-map[data-doc-id]')) {
    const meta = row.querySelector('.vault-map-meta');
    if (!meta || meta.querySelector('[data-storage-doc-id]')) continue;
    const size = document.createElement('span');
    size.className = 'vault-map-size';
    size.dataset.storageDocId = row.dataset.docId;
    size.textContent = t('vault.size.loading');
    meta.appendChild(size);
  }
  await updateDocumentSizes(container, State.getVaultStorage());
}

function invalidateAttachmentSizeCache() { invalidateStorageInfo(); }

/**
 * Preview (images) or download an attachment.
 */
async function previewOrDownload(docId, attachmentId, mimeType, filename) {
  const VaultStorage = State.getVaultStorage();
  // Only allow for owned docs (recipients don't manage attachments)
  const index = VaultStorage?.getIndex();
  const doc = index?.docs?.find(d => d.docId === docId);

  let dek;
  try {
    const dekBytes = await VaultStorage?.getAttachmentKeyBytes?.(docId);
    if (!dekBytes) throw new Error('No DEK');
    dek = await crypto.subtle.importKey(
      'raw', dekBytes, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']
    );
  } catch {
    showToast(t('vault.attachment.noKey'));
    return;
  }

  try {
    const { objectUrl, blob } = await getAttachmentUrl(docId, attachmentId, dek, { name: filename });

    if (mimeType?.startsWith('image/')) {
      // Show image preview modal
      showPreviewModal(objectUrl, filename, blob);
    } else {
      // Direct download for non-image files
      downloadFile(blob, filename);
    }
  } catch (err) {
    showToast(t('vault.attachment.loadFailed', { error: err.message || t('vault.error.unknown') }));
  }
}

async function downloadFile(blob, filename) {
  try {
    await withOperation(t('vault.attachment.saveTitle'), async progress => {
      await saveFileFromBlob(blob, filename || 'download', { onProgress: progress.update, throwOnError: true });
    }, () => [t('vault.attachment.readyToSave')]);
  } catch { /* The progress card retains the save error. */ }
}

function showPreviewModal(objectUrl, filename, blob) {
  // Remove any existing preview modal
  document.querySelector('.vault-preview-modal')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'vault-preview-modal';

  const modal = document.createElement('div');
  modal.className = 'vault-preview-content';

  const img = document.createElement('img');
  img.src = objectUrl;
  img.alt = filename || t('vault.attachment.preview');
  modal.appendChild(img);

  const actions = document.createElement('div');
  actions.className = 'vault-preview-actions';

  const downloadBtn = document.createElement('button');
  downloadBtn.className = 'vault-preview-btn';
  downloadBtn.textContent = t('common.download');
  downloadBtn.addEventListener('click', () => downloadFile(blob, filename));
  actions.appendChild(downloadBtn);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'vault-preview-btn cancel';
  closeBtn.textContent = t('common.close');
  closeBtn.addEventListener('click', closeModal);
  actions.appendChild(closeBtn);

  modal.appendChild(actions);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  function closeModal() {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  }

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeModal();
  });
  const onKey = (e) => {
    if (e.key === 'Escape') {
      e.stopImmediatePropagation();
      closeModal();
    }
  };
  document.addEventListener('keydown', onKey);
}

/**
 * Toggle the inline attachment panel for a document.
 */
async function toggleAttachmentPanel(docId, mapEl) {
  const wrapper = mapEl.closest('.vault-map-wrapper');
  const panel = wrapper?.querySelector('.vault-map-attachments');
  if (!panel) return;

  // Toggle: if already showing, collapse
  if (panel.innerHTML) {
    panel.innerHTML = '';
    return;
  }

  panel.innerHTML = `<div class="vault-attach-loading">${escapeHtml(t('vault.attachment.loading'))}</div>`;

  try {
    // Fetch the doc's DEK so listAttachments can decrypt filenames for display.
    // getDocDEK returns raw bytes; crypto.subtle wants a CryptoKey.
    const VaultStorage = State.getVaultStorage();
    let dekKey = null;
    try {
      // A map on this device has its own key (VaultStorage.getLocalDocKey).
      const dekBytes = await VaultStorage?.getAttachmentKeyBytes(docId);
      if (dekBytes) {
        dekKey = await crypto.subtle.importKey(
          'raw', dekBytes, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'],
        );
      }
    } catch { /* legacy / unshared */ }
    const attachments = await listAttachments(docId, dekKey);
    if (!panel.isConnected || !panel.querySelector('.vault-attach-loading')) return;
    const owner = !VaultStorage.getDoc(docId)?.role || VaultStorage.getDoc(docId).role === 'owner';
    await updateDocumentSizes(wrapper, VaultStorage);

    if (attachments.length === 0) {
      panel.innerHTML = `<div class="vault-map-attach-empty">${escapeHtml(t('vault.attachment.none'))}</div>`;
      return;
    }

    let html = '';
    for (const att of attachments) {
      const fileIcon = icon('clip');
      html += `<div class="vault-map-attach-file" data-attachment-id="${escapeAttr(att.attachmentId)}" data-doc-id="${escapeAttr(docId)}" data-mime="${escapeAttr(att.mimeType || '')}" data-filename="${escapeAttr(att.filename)}">
        <span class="vault-map-attach-icon">${fileIcon}</span>
        <button type="button" class="vault-map-attach-name" title="${escapeAttr(t('vault.attachment.openHint'))}">${escapeHtml(att.filename)}</button>
        <span class="vault-map-attach-size">${formatBytes(att.sizeOriginal)}</span>
        ${owner ? `<button class="vault-map-attach-delete" data-attachment-id="${escapeAttr(att.attachmentId)}" data-doc-id="${escapeAttr(docId)}" title="${escapeAttr(t('vault.attachment.delete'))}" aria-label="${escapeAttr(t('vault.attachment.deleteNamed', { name: att.filename }))}">${icon('trash')}</button>` : ''}
      </div>`;
    }
    panel.innerHTML = html;

    // Wire preview/download on name click
    panel.querySelectorAll('.vault-map-attach-name').forEach(nameEl => {
      nameEl.addEventListener('click', (e) => {
        e.stopPropagation();
        const row = nameEl.closest('.vault-map-attach-file');
        previewOrDownload(row.dataset.docId, row.dataset.attachmentId, row.dataset.mime, row.dataset.filename);
      });
    });

    // Wire delete buttons
    panel.querySelectorAll('.vault-map-attach-delete').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        if (!await confirmAction(t('vault.attachment.deleteConfirm'), { title: t('vault.attachment.delete'), submit: t('vault.attachment.delete'), danger: true })) return;
        try {
          await deleteAttachment(btn.dataset.docId, btn.dataset.attachmentId);
          // Reload: clear panel so toggleAttachmentPanel enters the "load" branch
          invalidateAttachmentSizeCache(docId);
          panel.innerHTML = '';
          await toggleAttachmentPanel(docId, mapEl);
        } catch (err) {
          showToast(t('vault.attachment.deleteFailed', { error: err.message || t('vault.error.unknownShort') }));
        }
      });
    });
  } catch (err) {
    panel.innerHTML = `<div class="vault-map-attach-empty">${escapeHtml(t('vault.attachment.listFailed'))}</div>`;
  }
}

/**
 * Handle clicks on vault container
 * @param {Event} e - Click event
 */
function handleContainerClick(e) {
  const target = e.target.closest('button, summary') || e.target;
  const actionButton = target.closest('[data-action]');
  if (actionButton) {
    if (actionButton.dataset.folderId) Actions.handleFolderAction(actionButton, actionButton.dataset.folderId);
    else if (actionButton.dataset.shareId) {
      if (actionButton.dataset.action === 'leave-share') Actions.handleSharedAction(actionButton, actionButton.dataset.shareId);
      else Actions.handleOwnedShareAction(actionButton, actionButton.dataset.shareId);
    } else Actions.handleMapAction(actionButton, actionButton.dataset.docId);
    actionButton.closest('details')?.removeAttribute('open');
    return;
  }
  if (target.closest('summary')) return;
  const VaultStorage = State.getVaultStorage();

  // Category folder selection (Shared with me, Shared with others)
  if (target.closest(".vault-category-row")) {
    const item = target.closest(".vault-category-row");
    const categoryId = item.dataset.categoryId;
    State.setSelectedFolderId(categoryId);
    State.setSearchQuery("");
    render();
    return;
  }

  // Sort button clicks
  if (target.classList.contains("vault-sort-btn")) {
    const sortBy = target.dataset.sort;
    const currentSortBy = State.getSortBy();
    const currentSortOrder = State.getSortOrder();

    if (currentSortBy === sortBy) {
      // Toggle order if same sort field
      State.setSortOrder(currentSortOrder === "asc" ? "desc" : "asc");
    } else {
      State.setSortBy(sortBy);
      State.setSortOrder(sortBy === "date" ? "desc" : "asc"); // Default: date desc, name asc
    }
    render();
    return;
  }

  // Search scope toggle
  if (target.classList.contains("vault-scope-btn")) {
    State.setSearchScope(target.dataset.scope);
    render();
    return;
  }

  // Folder expand/collapse toggle
  if (target.classList.contains("vault-folder-expand")) {
    const folderId = target.dataset.folderId;
    Actions.toggleFolder(folderId);
    return;
  }

  // Folder selection
  if (target.closest(".vault-folder-row")) {
    const item = target.closest(".vault-folder-row");
    const folderId = item.dataset.folderId;

    // If clicking actions, don't select
    if (target.closest(".vault-folder-actions")) {
      Actions.handleFolderAction(target, folderId);
      return;
    }

    State.setSelectedFolderId(folderId);
    State.setSearchQuery(""); // Clear search when selecting folder
    render();
    return;
  }

  // Map item click (includes shared maps in file list)
  if (target.closest(".vault-map")) {
    const item = target.closest(".vault-map");

    // Handle shared map items (from "Shared with me" category)
    if (item.classList.contains("vault-shared-map")) {
      const shareId = item.dataset.shareId;
      if (target.closest(".vault-map-actions")) {
        Actions.handleSharedAction(target, shareId);
        return;
      }
      Actions.openDoc(shareId);
      return;
    }

    // Handle owned share items (from "Shared with others" category)
    if (item.classList.contains("vault-owned-share")) {
      const shareId = item.dataset.shareId;
      if (target.closest(".vault-map-actions")) {
        Actions.handleOwnedShareAction(target, shareId);
        return;
      }
      // Open the shared document directly (share ID is also the document ID)
      Actions.openDoc(shareId);
      return;
    }

    // Regular map
    const docId = item.dataset.docId;

    // Expand triangle — toggle attachment panel
    if (target.classList.contains("vault-map-expand")) {
      target.classList.toggle('expanded');
      target.setAttribute('aria-expanded', String(target.classList.contains('expanded')));
      toggleAttachmentPanel(docId, item);
      return;
    }

    // If clicking actions, don't open
    if (target.closest(".vault-map-actions")) {
      Actions.handleMapAction(target, docId);
      return;
    }

    if (VaultStorage.getDoc(docId)?.role && VaultStorage.getDoc(docId).role !== 'owner') Actions.openDoc(docId);
    else Actions.openPersonalDoc(docId);
    return;
  }

  // Search folder click
  if (target.closest(".vault-search-folder")) {
    const item = target.closest(".vault-search-folder");
    const folderId = item.dataset.folderId;
    State.setSelectedFolderId(folderId);
    State.setSearchQuery("");
    render();
    return;
  }

  // Search result: shared with me
  if (target.closest(".vault-search-share")) {
    const item = target.closest(".vault-search-share");
    const shareId = item.dataset.shareId;
    Actions.openDoc(shareId);
    return;
  }

  // Search result: owned share
  if (target.closest(".vault-search-owned-share")) {
    const item = target.closest(".vault-search-owned-share");
    const shareId = item.dataset.shareId;
    Actions.openDoc(shareId);
    return;
  }

  // Breadcrumb navigation
  if (
    target.classList.contains("vault-breadcrumb") &&
    !target.classList.contains("ellipsis")
  ) {
    const folderId = target.dataset.folderId;
    State.setSelectedFolderId(folderId);
    render();
    return;
  }

  // Toolbar buttons
  if (target.id === "vault-go-up") {
    const index = VaultStorage.getIndex();
    const selectedFolderId = State.getSelectedFolderId();
    const current = index.folders.find((f) => f.id === selectedFolderId);
    if (current?.parentId) {
      State.setSelectedFolderId(current.parentId);
      render();
    }
    return;
  }

  if (target.id === "vault-add-folder") {
    Actions.promptNewFolder(State.getSelectedFolderId());
    return;
  }

  if (target.id === "vault-add-map") {
    Actions.promptNewDocInFolder(State.getSelectedFolderId());
    return;
  }

  if (target.id === "vault-new-folder") {
    Actions.promptNewFolder(null);
    return;
  }

  // Export data button
  if (target.id === "vault-export-data" || target.closest("#vault-export-data")) {
    Actions.handleExportData();
    return;
  }

  // Import data button
  if (target.id === "vault-import-data" || target.closest("#vault-import-data")) {
    document.getElementById("vault-import-file")?.click();
    return;
  }
}

// ============================================
// Drag and Drop
// ============================================

/**
 * Handle drag start
 * @param {DragEvent} e - Drag event
 */
function handleDragStart(e) {
  if (!e.target.closest(".vault-map")) return;

  const item = e.target.closest(".vault-map");

  // Check if it's a shared map being dragged
  if (item.classList.contains("vault-shared-map")) {
    State.setDraggedShareId(item.dataset.shareId);
    State.setDraggedDocId(null);
    e.dataTransfer.effectAllowed = "copy"; // Copy, not move
  } else {
    State.setDraggedDocId(item.dataset.docId);
    State.setDraggedShareId(null);
    e.dataTransfer.setData("text/plain", item.dataset.docId);
    e.dataTransfer.effectAllowed = "move";
  }
}

/**
 * Handle drag end
 * @param {DragEvent} e - Drag event
 */
function handleDragEnd(e) {
  State.setDraggedDocId(null);
  State.setDraggedShareId(null);
  // Remove all drop highlights
  document.querySelectorAll(".vault-folder-row, .vault-child-folder").forEach((el) => {
    el.classList.remove("drag-over");
  });
}

/**
 * Handle drag over
 * @param {DragEvent} e - Drag event
 */
function handleDragOver(e) {
  const draggedDocId = State.getDraggedDocId();
  const draggedShareId = State.getDraggedShareId();

  if (!draggedDocId && !draggedShareId) return;

  const folder = e.target.closest(".vault-folder-row, .vault-child-folder");
  if (folder) {
    try { destinationFolder(State.getVaultStorage().getIndex(), State.getVaultStorage().getDoc(draggedDocId || draggedShareId), folder.dataset.folderId); }
    catch { return; }
    e.preventDefault();
    e.dataTransfer.dropEffect = draggedShareId ? "copy" : "move";

    // Highlight drop target
    document.querySelectorAll(".vault-folder-row, .vault-child-folder").forEach((el) => {
      el.classList.remove("drag-over");
    });
    folder.classList.add("drag-over");
  }
}

/**
 * Handle drop
 * @param {DragEvent} e - Drag event
 */
function handleDrop(e) {
  const folder = e.target.closest(".vault-folder-row, .vault-child-folder");
  if (!folder) return;

  e.preventDefault();
  const folderId = folder.dataset.folderId;
  const draggedShareId = State.getDraggedShareId();
  const draggedDocId = State.getDraggedDocId();

  // Handle dropping a shared map into a folder (create local copy)
  if (draggedShareId) {
    Actions.copySharedMapToFolder(draggedShareId, folderId);
    State.setDraggedShareId(null);
  } else if (draggedDocId) {
    Actions.moveMap(draggedDocId, folderId);
    State.setDraggedDocId(null);
  }

  // Remove drop highlights
  document.querySelectorAll(".vault-folder-row, .vault-child-folder").forEach((el) => {
    el.classList.remove("drag-over");
  });
}

// ============================================
// Resize Handling
// ============================================

/**
 * Attach resize listeners
 */
function attachResizeListeners() {
  const handles = document.querySelectorAll(".vault-resize-handle");
  handles.forEach((handle) => {
    handle.addEventListener("mousedown", startResize);
  });

  // Panel resize
  const panelResize = document.querySelector(".vault-panel-resize");
  if (panelResize) {
    panelResize.removeEventListener("mousedown", startPanelResize);
    panelResize.addEventListener("mousedown", startPanelResize);
  }
}

/**
 * Start column resize
 * @param {MouseEvent} e - Mouse event
 */
function startResize(e) {
  const handle = e.target;
  const resizeType = handle.dataset.resize;
  const startX = e.clientX;
  e.preventDefault();
  const startWidth = document.getElementById(`vault-col-${resizeType}`)?.getBoundingClientRect().width || 220;

  function onMouseMove(e) {
    const delta = e.clientX - startX;
    let newWidth;

    if (resizeType === "folders") {
      newWidth = Math.max(180, Math.min(320, startWidth + delta));
      document.getElementById("vault-col-folders").style.width =
        `${newWidth}px`;
    } else if (resizeType === "recent") {
      newWidth = Math.max(150, Math.min(400, startWidth - delta));
      document.getElementById("vault-col-recent").style.width = `${newWidth}px`;
    }
  }

  function onMouseUp() {
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
    State.saveSizes();
  }

  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("mouseup", onMouseUp);
}

/**
 * Start panel resize
 * @param {MouseEvent} e - Mouse event
 */
function startPanelResize(e) {
  const panel = document.getElementById("vault-panel");
  const startX = e.clientX;
  e.preventDefault();
  const startWidth = panel.getBoundingClientRect().width;

  function onMouseMove(e) {
    const delta = e.clientX - startX;
    const newWidth = Math.max(
      500,
      Math.min(window.innerWidth * 0.95, startWidth + delta),
    );
    panel.style.width = `${newWidth}px`;
  }

  function onMouseUp() {
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
    State.saveSizes();
  }

  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("mouseup", onMouseUp);
}
